import {Box, Flex, InputLeftElement, useOutsideClick} from '@chakra-ui/react';
import SearchSvg from 'assets/SVG/SearchSvg';
import {colors} from 'components/providers';
import {InputSearchWithDebounce} from 'components/shared';
import {useTranslation} from 'hooks';
import React, {FC, useRef, useState} from 'react';
import {ISearchAutocompleteItem} from 'types';

import {SearchResults} from './components';

interface IProps {
  suggestions?: ISearchAutocompleteItem[];
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onItemClick?: (item: ISearchAutocompleteItem) => void;
  heightLG?: boolean;
}

export const SearchInput: FC<IProps> = React.memo(
  ({suggestions = [], placeholder, value, onChange, onItemClick, heightLG}) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const {t} = useTranslation();
    const ref = useRef<any>();

    useOutsideClick({
      ref,
      handler: () => setShowSuggestions(false),
    });

    const onClearSearchField = () => {
      onChange('');
      setShowSuggestions(false);
    };

    const handleInputChangeWithDebounce = (value: string) => {
      onChange(value);
      setShowSuggestions(value.length > 1);
    };

    const handleItemClick = (item: ISearchAutocompleteItem) => {
      if (!onItemClick) return;
      onItemClick(item);
      setShowSuggestions(false);
    };

    const handleContainerFocus = () => setShowSuggestions(value.length > 1);
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
    };

    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        setActiveSuggestionIndex((prevIndex) => Math.min(prevIndex + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        setActiveSuggestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
      } else if (e.key === 'Enter') {
        const [searchItem] = suggestions;
        const searchItemQuery = activeSuggestionIndex ? suggestions[activeSuggestionIndex] : searchItem;
        onItemClick && onItemClick(searchItemQuery);
      } else {
        setActiveSuggestionIndex(0);
      }
    };

    return (
      <Box position="relative" zIndex={9} ref={ref} onFocus={handleContainerFocus}>
        <form onSubmit={handleSubmit}>
          <InputSearchWithDebounce
            searchValue={value}
            placeholder={placeholder ?? t('SEARCH')}
            onChangeValueWithDebounce={handleInputChangeWithDebounce}
            onClearSearchField={onClearSearchField}
            delay={10}
            cssPropsForInput={{
              borderColor: '#EBECF1',
              _focus: {border: '1px solid black', opacity: ''},
              _placeholder: {
                fontWeight: 600,
                color: colors.black,
              },
              pl: '56px',
              backgroundColor: colors.white,
              color: colors.black,
              height: heightLG ? '78px' : '46px',
            }}
            onKeyDown={handleKeyDown}
          >
            <InputLeftElement pointerEvents="none" h="full">
              <Flex ml="18px" alignItems="center" h="full">
                <SearchSvg fill={colors.black} />
              </Flex>
            </InputLeftElement>
          </InputSearchWithDebounce>
        </form>
        {!!suggestions.length && showSuggestions && (
          <Box>
            <SearchResults
              suggestions={suggestions}
              activeSuggestionIndex={activeSuggestionIndex}
              onItemClick={handleItemClick}
              topPadding={heightLG ? '78px' : '46px'}
            />
          </Box>
        )}
      </Box>
    );
  },
);
